import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'

import {
  getProductVendorsApi,
  getProductDetailApi,
  patchProductVendorPreferenceApi,
  deleteProductVendorApi,
  getPicSourcingApi,
  postProductVendorApi,
  putProductVendorApi,
  getProductVendorCounts,
  getProductVendorDetailsApi,
  type PostProductVendorDataType,
  type PutProductVendorDataType,
} from '../../services/productVendor'
import {
  sliceName,
  setLoading,
  setProductVendors,
  setProductDetails,
  setPicSourcingList,
  setProductVendorDetails,
} from './productVendorsSlice'

export const fetchProductVendors = createAsyncThunk(
  `${sliceName}/fetchProductVendors`,
  async (
    { productId, cursor, vendorName }: { productId: number; cursor?: string; vendorName?: string },
    { dispatch },
  ) => {
    dispatch(setLoading(true))
    try {
      const { data } = await getProductVendorsApi(productId, cursor, vendorName)
      dispatch(setProductVendors(data))
    } catch (error) {
      throw callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchProductDetails = createAsyncThunk(
  `${sliceName}/fetchProductDetails`,
  async (productId: number, { dispatch }) => {
    try {
      const { data } = await getProductDetailApi(productId)
      dispatch(setProductDetails(data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchProductVendorDetails = createAsyncThunk(
  `${sliceName}/fetchProductVendorDetails`,
  async (vendorId: number, { dispatch }) => {
    try {
      const { data } = await getProductVendorDetailsApi(vendorId)
      if (data.data) {
        const updatedData = {
          ...data.data,
          vendor: {
            ...data.data.vendor,
            company_name: data.data.vendor.name,
          },
          pic_sourcing: {
            ...data.data.pic_sourcing,
            full_name: data.data.pic_sourcing.name,
          },
        }
        dispatch(setProductVendorDetails(updatedData))
      }
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const patchMainVendor = createAsyncThunk(
  `${sliceName}/patchMainVendor`,
  async (productId: number, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await patchProductVendorPreferenceApi(productId)
      toastSuccess(`Berhasil mengatur vendor utama`)
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const deleteProductVendor = createAsyncThunk(
  `${sliceName}/deleteProductVendor`,
  async (productId: number, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await deleteProductVendorApi(productId)
      toastSuccess(`Vendor berhasil dihapus`)
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const postProductVendor = createAsyncThunk(
  `${sliceName}/postProductVendor`,
  async (data: PostProductVendorDataType, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await postProductVendorApi(data)
      toastSuccess(`Vendor berhasil ditambahkan`)
      return true
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const putProductVendor = createAsyncThunk(
  `${sliceName}/putProductVendor`,
  async (
    { vendorId, data }: { vendorId: string; data: PutProductVendorDataType },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await putProductVendorApi(vendorId, data)
      toastSuccess(`Vendor berhasil diperbarui`)
      return true
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchPicSourcing = createAsyncThunk(
  `${sliceName}/fetchPicSourcing`,
  async (name: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getPicSourcingApi(name)
      dispatch(setPicSourcingList(data.content))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchProductVendorsCount = createAsyncThunk(
  `${sliceName}/fetchProductVendorsCount`,
  async (lcoationIds: number[], { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getProductVendorCounts(lcoationIds?.join(','))
      return data
    } catch (error) {
      callErrorMsg(error)
      return error
    } finally {
      dispatch(setLoading(false))
    }
  },
)
